import type { DebugLogger } from '@iconik-io/core/src/debug_logger/debug_logger';

import type { AudioChannel } from '../../types';

function isAudioChannel(value: unknown): value is AudioChannel {
  return (
    typeof value === 'object' &&
    value !== null &&
    'volume' in value &&
    typeof value.volume === 'number' &&
    'pan' in value &&
    typeof value.pan === 'number' &&
    'isMuted' in value &&
    typeof value.isMuted === 'boolean' &&
    'isSolo' in value &&
    typeof value.isSolo === 'boolean'
  );
}

export const booleanEncoder = {
  encode(value: boolean) {
    return value ? 'true' : 'false';
  },
  decode(value: string) {
    return value === 'true';
  },
};

export const genericEncoder = {
  encode: JSON.stringify,
  decode: JSON.parse,
};

export const numberEncoder = {
  encode(value: number) {
    return value.toString();
  },
  decode(value: string) {
    return Number(value);
  },
};

export function createPersistentAudioChannelsEncoder(
  logger: DebugLogger,
  initialAudioChannels: AudioChannel[],
) {
  return {
    encode: JSON.stringify,
    /** Decode and validate persisted audio channels. */
    decode(value: string): AudioChannel[] {
      try {
        const parsed: unknown = JSON.parse(value);
        if (!Array.isArray(parsed)) {
          throw new Error('Invalid audio channels format');
        }
        if (parsed.length !== initialAudioChannels.length) {
          throw new Error(
            `Invalid audio channels length: expected ${initialAudioChannels.length}, got ${parsed.length}`,
          );
        }

        const channels: AudioChannel[] = [];
        for (const channel of parsed) {
          if (!isAudioChannel(channel)) {
            throw new Error('Invalid audio channel format');
          }

          if (channel.volume < 0 || channel.volume > 1) {
            throw new Error('Invalid audio channel volume');
          }

          if (channel.pan < -1 || channel.pan > 1) {
            throw new Error('Invalid audio channel pan');
          }

          channels.push(channel);
        }

        return channels;
      } catch (error) {
        logger.debug(
          'Failed to decode audio channels, using default. Error:',
          error,
        );
        return initialAudioChannels;
      }
    },
  };
}
